import type { HttpClient } from './httpClient';
import type {
  StreamlakeCatalogTableResponse,
  StreamlakeCatalogTreeResponse,
  StreamlakeConnectionCheckResponse,
  StreamlakeDataQualityResponse,
  StreamlakeFeatureProperty,
  StreamlakeIdentityField,
  StreamlakeOverviewResponse,
  StreamlakePipeline,
  StreamlakePipelineDetailResponse,
  StreamlakePipelineWizardOptionsResponse,
  StreamlakeProfileDetailResponse,
  StreamlakeProfileFilter,
  StreamlakeSamplingPolicy,
  StreamlakeTableNameCheckResponse,
  StreamlakeUpsOverviewResponse,
  CreateStreamlakePipelineRequest,
  CreateStreamlakeSamplingPolicyRequest,
  ListStreamlakeFeaturePropertiesResponse,
  ListStreamlakeIdentityFieldsResponse,
  ListStreamlakePipelinesResponse,
  ListStreamlakeSamplingPoliciesResponse,
  ListStreamlakeSourcePipelinesResponse,
  StreamlakeOnlineStoreResponse,
  RunStreamlakeQueryRequest,
  RunStreamlakeQueryResponse,
  SearchStreamlakeProfilesResponse,
  SimulateStreamlakeSamplingPolicyRequest,
  SimulateStreamlakeSamplingPolicyResponse,
  UpdateStreamlakeOnlineStoreRequest,
  UpdateStreamlakeSamplingPolicyRequest,
} from './streamlakeTypes';

interface SwrResult<T> {
  body: T | undefined;
  isLoading: boolean;
  error: unknown;
  mutate: () => Promise<unknown>;
}

// The Data Platform domain has no generated SDK yet, so every call goes
// through the raw httpClient against hand-built URLs (same ship-ahead-of-
// codegen pattern as gdsg.ts). Swap to generated Api classes once the domain
// lands in @repo/api-gw-sdk.
export const streamlake = (
  httpClient: HttpClient,
  currentProjectId: string
) => {
  const root = `/projects/${currentProjectId}/streamlake`;
  const get = <T>(url: string | null) =>
    httpClient.get(url, { shouldRetryOnError: false }) as SwrResult<T>;

  return {
    overview: {
      useGet: () => get<StreamlakeOverviewResponse>(`${root}/overview`),
    },
    pipelines: {
      useList: () => get<ListStreamlakePipelinesResponse>(`${root}/pipelines`),
      useGet: (pipelineId: string | undefined) =>
        get<StreamlakePipelineDetailResponse>(
          pipelineId ? `${root}/pipelines/${pipelineId}` : null
        ),
      useDataQuality: (pipelineId: string | undefined) =>
        get<StreamlakeDataQualityResponse>(
          pipelineId ? `${root}/pipelines/${pipelineId}/data-quality` : null
        ),
      useWizardOptions: (enabled: boolean) =>
        get<StreamlakePipelineWizardOptionsResponse>(
          enabled ? `${root}/pipelines/wizard-options` : null
        ),
      create: (
        body: CreateStreamlakePipelineRequest
      ): Promise<StreamlakePipeline> =>
        httpClient.post(`${root}/pipelines`, body),
      update: (
        pipelineId: string,
        body: CreateStreamlakePipelineRequest
      ): Promise<StreamlakePipeline> =>
        httpClient.patch(`${root}/pipelines/${pipelineId}`, body),
      validateSource: (body: {
        sourceKind: string;
        cluster: string;
        topic: string;
      }): Promise<StreamlakeConnectionCheckResponse> =>
        httpClient.post(`${root}/pipelines/validate-source`, body),
      testCredentials: (body: {
        credential: string;
      }): Promise<StreamlakeConnectionCheckResponse> =>
        httpClient.post(`${root}/pipelines/test-credentials`, body),
      validateTable: (body: {
        namespace: string;
        table: string;
      }): Promise<StreamlakeTableNameCheckResponse> =>
        httpClient.post(`${root}/pipelines/validate-table`, body),
    },
    samplingPolicies: {
      useList: () =>
        get<ListStreamlakeSamplingPoliciesResponse>(
          `${root}/sampling-policies`
        ),
      create: (
        body: CreateStreamlakeSamplingPolicyRequest
      ): Promise<StreamlakeSamplingPolicy> =>
        httpClient.post(`${root}/sampling-policies`, body),
      update: (
        policyId: string,
        body: UpdateStreamlakeSamplingPolicyRequest
      ): Promise<StreamlakeSamplingPolicy> =>
        httpClient.patch(`${root}/sampling-policies/${policyId}`, body),
      simulate: (
        body: SimulateStreamlakeSamplingPolicyRequest
      ): Promise<SimulateStreamlakeSamplingPolicyResponse> =>
        httpClient.post(`${root}/sampling-policies/simulate`, body),
    },
    ups: {
      useOverview: () =>
        get<StreamlakeUpsOverviewResponse>(`${root}/ups/overview`),
      sourcePipelines: {
        useList: () =>
          get<ListStreamlakeSourcePipelinesResponse>(
            `${root}/ups/source-pipelines`
          ),
        setAttached: (
          pipelineId: string,
          attached: boolean
        ): Promise<{ pipelineId: string; attached: boolean }> =>
          httpClient.patch(`${root}/ups/source-pipelines/${pipelineId}`, {
            attached,
          }),
      },
      identityFields: {
        useList: () =>
          get<ListStreamlakeIdentityFieldsResponse>(
            `${root}/ups/identity-fields`
          ),
        replace: (
          fields: StreamlakeIdentityField[]
        ): Promise<{ fields: StreamlakeIdentityField[] }> =>
          httpClient.put(`${root}/ups/identity-fields`, { fields }),
      },
      features: {
        useList: () =>
          get<ListStreamlakeFeaturePropertiesResponse>(`${root}/ups/features`),
        create: (
          body: Omit<StreamlakeFeatureProperty, 'id' | 'comments'>
        ): Promise<StreamlakeFeatureProperty> =>
          httpClient.post(`${root}/ups/features`, body),
        update: (
          featureId: string,
          body: Partial<Omit<StreamlakeFeatureProperty, 'id' | 'comments'>>
        ): Promise<StreamlakeFeatureProperty> =>
          httpClient.patch(`${root}/ups/features/${featureId}`, body),
        remove: (featureId: string): Promise<{ id: string }> =>
          httpClient.delete(`${root}/ups/features/${featureId}`),
        addComment: (
          featureId: string,
          text: string
        ): Promise<StreamlakeFeatureProperty> =>
          httpClient.post(`${root}/ups/features/${featureId}/comments`, {
            text,
          }),
      },
      onlineStore: {
        useGet: () =>
          get<StreamlakeOnlineStoreResponse>(`${root}/ups/online-store`),
        update: (
          body: UpdateStreamlakeOnlineStoreRequest
        ): Promise<StreamlakeOnlineStoreResponse> =>
          httpClient.put(`${root}/ups/online-store`, body),
      },
      profiles: {
        search: (
          filters: StreamlakeProfileFilter[]
        ): Promise<SearchStreamlakeProfilesResponse> =>
          httpClient.post(`${root}/ups/profiles/search`, { filters }),
        useGet: (profileId: string | undefined) =>
          get<StreamlakeProfileDetailResponse>(
            profileId
              ? `${root}/ups/profiles/${encodeURIComponent(profileId)}`
              : null
          ),
      },
    },
    catalog: {
      useTree: () => get<StreamlakeCatalogTreeResponse>(`${root}/catalog/tree`),
      useTable: (tableId: string | undefined) =>
        get<StreamlakeCatalogTableResponse>(
          tableId ? `${root}/catalog/tables/${tableId}` : null
        ),
    },
    query: {
      run: (
        body: RunStreamlakeQueryRequest
      ): Promise<RunStreamlakeQueryResponse> =>
        httpClient.post(`${root}/query`, body),
    },
  };
};
